/* ══════════════════════════════════════════════════════════════════
   Einmal je Auftrag heißt einmal — Durchgang 03.10.2026 (AG-03, V-20).

   Ein Zuschlag mit `scope = 'auftrag'` (die Anlieferung) gilt dem ganzen
   Vorgang, nicht jedem Gerät darin. Der Server zählt ihn so
   (`fach.zuschlaegeZusammenfassen`, Rechenreferenz 7); in der Oberfläche
   stand er an jeder Mietzeile und wurde je Zeile gezählt — am Telefon
   „Dazu kommen 270,00" statt 255,00 — und je Gerät als Zeile von Hand
   angelegt, zweimal im Auftrag bei zwei Radladern.

   Eine Stelle für alle drei Wege (Entwurf am Telefon, Abschluss der
   Merkliste, Angebot aus der Merkliste), damit die Regel nicht an einer
   steht und an der nächsten nicht (CLAUDE.md, Prüfung vom 19./20.09.).
   Ohne Abhängigkeiten, damit sie sich ohne Browser prüfen lässt.
   ══════════════════════════════════════════════════════════════════ */

/**
 * Lässt von den Zeilen „einmal je Auftrag" nur die erste je Code durch.
 * `schon` trägt über mehrere Aufrufe mit, welche Codes schon da waren — so
 * gilt die Regel über alle Mieten eines Vorgangs, nicht nur innerhalb einer.
 */
export function einmalJeAuftrag<T>(
  zeilen: readonly T[],
  merkmal: (z: T) => { code: string | undefined; jeAuftrag: boolean },
  schon: Set<string> = new Set(),
): T[] {
  return zeilen.filter((z) => {
    const { code, jeAuftrag } = merkmal(z);
    if (!jeAuftrag || !code) return true;
    if (schon.has(code)) return false;
    schon.add(code);
    return true;
  });
}

/**
 * **Welche „einmal je Auftrag"-Zuschläge an diesem Posten nicht mehr zählen**
 * (V-20-Nebenbefund, Nachprüfung 03.10.2026).
 *
 * Merkliste und Abschluss rechneten die Anlieferung je Posten: Holzhäcksler
 * 1.285,00 und Rüttler 315,00, „Summe netto 1.600,00" — die AB sagte danach
 * 1.585,00, weil der Server sie einmal rechnet. Jetzt gehört ein Code dem
 * ersten Posten, der ihn trägt; an jedem weiteren steht er in der Menge, die
 * diese Funktion liefert. Ist er an **irgendeinem** Posten abgewählt („Kunde
 * holt ab"), zählt er an keinem — eine Zeile 0,00 von Hand schlägt die Regel
 * am ganzen Vorgang (Rechenreferenz R12), so rechnet auch der Server.
 */
export function jeAuftragSchonGezaehlt<P>(
  posten: readonly P[],
  zuschlaege: (p: P) => readonly { code: string; jeAuftrag?: boolean; aus?: boolean }[],
): Map<P, Set<string>> {
  const abgewaehlt = new Set<string>();
  for (const p of posten) {
    for (const z of zuschlaege(p)) if (z.jeAuftrag && z.aus) abgewaehlt.add(z.code);
  }
  const vergeben = new Set<string>();
  const ergebnis = new Map<P, Set<string>>();
  for (const p of posten) {
    const nicht = new Set<string>(abgewaehlt);
    for (const z of zuschlaege(p)) {
      if (!z.jeAuftrag) continue;
      if (vergeben.has(z.code)) nicht.add(z.code);
      else vergeben.add(z.code);
    }
    ergebnis.set(p, nicht);
  }
  return ergebnis;
}
